/**
 * API client for same-domain communication with the Pharmacy Clinical Copilot backend.
 * Uses /api/* paths through CloudFront routing to ALB.
 * Stage-agnostic - works with any environment through relative URLs.
 */

const API_BASE_URL = '/api';

// Default per-request timeout. Without this, a fetch that never settles
// (dropped connection during a token/session renewal, network transition,
// laptop sleep/resume) hangs forever. Callers that `await` such a request
// in a loop — notably the shoulder-tap poll loop — would wedge permanently
// with no way to recover. The timeout guards time-to-response-headers, so
// streaming endpoints are unaffected once headers arrive.
//
// Kept well under the poll cadence (15s): missing a single poll is cheap,
// but a request that hung near the interval would push the whole loop
// permanently behind, so we abort early and let the next tick fire on time.
const DEFAULT_TIMEOUT_MS = 5_000;

type ApiRequestOptions = {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE';
  body?: unknown;
  accept?: string;
  timeoutMs?: number;
};

async function apiRequest(
  path: string,
  {
    method = 'POST',
    body,
    accept = 'application/json',
    timeoutMs = DEFAULT_TIMEOUT_MS,
  }: ApiRequestOptions = {},
): Promise<Response | null> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    const response = await fetch(`${API_BASE_URL}${path}`, {
      method,
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
        'Accept': accept,
      },
      body: body === undefined ? undefined : JSON.stringify(body),
      signal: controller.signal,
    });
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }
    return response;
  } catch (error) {
    console.error(`Error in ${method} ${path}:`, error);
    return null;
  } finally {
    // Clear as soon as headers are in (or the request failed/aborted) so we
    // never abort a response body mid-stream, and so the timer can't leak.
    clearTimeout(timeoutId);
  }
}

async function apiRequestJson<T>(
  path: string,
  options?: ApiRequestOptions,
): Promise<T | null> {
  const response = await apiRequest(path, options);
  if (!response) return null;
  try {
    return (await response.json()) as T;
  } catch (error) {
    console.error(`Error parsing JSON from ${path}:`, error);
    return null;
  }
}

export interface UserInfo {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  posixGroups: string[];
  cognitoGroups: string[];
  userId?: string;
  provider?: string;
  authenticated: boolean;
  headers: Record<string, string>;
  cookies: Record<string, string>;
  timestamp: string;
}

export interface WebSocketCredentials {
  accessKeyId: string;
  secretAccessKey: string;
  sessionToken: string;
  websocketUrl: string;
  region: string;
}

export interface FeedbackData {
  session_id: string | null;
  prompt: string;
  response: string;
  is_helpful: boolean;
  notes?: string | null;
  not_helpful_reason?: string | null;
}

export interface FeedbackResponse {
  success: boolean;
  message?: string;
}

export interface ShoulderTapConfiguration {
  enabled: boolean;
  poll_interval_seconds: number;
}

export interface ShoulderTapPayload {
  message: string;
  reason: string;
}

export interface ShoulderTapAnalysis {
  contact_id: string;
  tap: ShoulderTapPayload | null;
}

export interface ShoulderTapAckResponse {
  status: string;
}

export const api = {
  /**
   * Get current user information from the /me endpoint.
   * Returns user info if authenticated, or unauthenticated state if not.
   */
  async getMe(): Promise<UserInfo | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/me`, {
        credentials: 'include', // Ensure cookies are sent for authentication
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          console.log('User not authenticated - login required');
          return {
            authenticated: false,
            firstName: undefined,
            lastName: undefined,
            email: undefined,
            username: undefined,
            posixGroups: [],
            cognitoGroups: [],
            userId: undefined,
            provider: undefined,
            headers: {},
            cookies: {},
            timestamp: new Date().toISOString()
          };
        }
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const userData = await response.json();
      console.log('User authenticated:', userData.username || userData.email);
      return userData;
    } catch (error) {
      console.error('Error fetching user info:', error);
      // Return unauthenticated state for any error
      return {
        authenticated: false,
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        username: undefined,
        posixGroups: [],
        cognitoGroups: [],
        userId: undefined,
        provider: undefined,
        headers: {},
        cookies: {},
        timestamp: new Date().toISOString()
      };
    }
  },

  saveFeedback(feedbackData: FeedbackData): Promise<Response | null> {
    return apiRequest('/saveFeedback', { body: feedbackData });
  },

  summarizeCustomerPreviousInteraction(contact_id: string): Promise<Response | null> {
    return apiRequest('/summarizeCustomerPreviousInteraction', {
      body: { contact_id },
      accept: 'text/event-stream',
    });
  },

  getShoulderTapConfiguration(contact_id: string): Promise<ShoulderTapConfiguration | null> {
    return apiRequestJson<ShoulderTapConfiguration>('/shoulder-tap-configuration', {
      body: { contact_id },
    });
  },

  getShoulderTapAnalysis(contact_id: string): Promise<ShoulderTapAnalysis | null> {
    return apiRequestJson<ShoulderTapAnalysis>('/shoulder-tap-analysis', {
      body: { contact_id },
    });
  },

  acceptShoulderTap(contact_id: string): Promise<ShoulderTapAckResponse | null> {
    return apiRequestJson<ShoulderTapAckResponse>('/shoulder-tap-accept', {
      body: { contact_id },
    });
  },

  dismissShoulderTap(contact_id: string): Promise<ShoulderTapAckResponse | null> {
    return apiRequestJson<ShoulderTapAckResponse>('/shoulder-tap-dismiss', {
      body: { contact_id },
    });
  },

  getWebSocketCredentials(): Promise<WebSocketCredentials | null> {
    return apiRequestJson<WebSocketCredentials>('/auth/websocket-credentials', { method: 'GET' });
  },
};
